import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Badge,
  datePresetOptions,
  FilterBar,
  FilterControlType,
  KpiTile,
  Page,
  SearchInput,
  SearchSpinner,
  SearchTable,
  type BooleanFilterDefinition,
  type DateFilterDefinition,
  type MultiselectFilterDefinition,
} from 'corso-admin-ui';
import {
  RollingDatePreset,
  Sidekick,
  type CommandAdminApi,
  type ViewDateValue,
} from 'corso-types';
import { formatter } from 'corso-web-lib';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '~/api';
import { sharedTableDefinitions } from '~/components/dataTable';

type Conversation = CommandAdminApi.ResponseBody<
  '/sidekick/conversation/search',
  'post'
>['conversations'][number];

const { createdOnColumn } = sharedTableDefinitions;

const sentimentVariant = {
  Positive: 'success',
  Neutral: 'default',
  Negative: 'danger',
} as const satisfies Record<Sidekick.UserSentiment, string>;

const filters: {
  createdOn: DateFilterDefinition;
  sentiments: MultiselectFilterDefinition;
  isCorsoUser: BooleanFilterDefinition;
} = {
  createdOn: {
    id: 'createdOn',
    type: FilterControlType.date,
    label: 'Date',
    verb: 'is',
    options: datePresetOptions,
  },
  sentiments: {
    id: 'sentiments',
    type: FilterControlType.multiselect,
    label: 'Feedback',
    verb: 'is',
    placeholder: 'Search feedback',
    options: Object.values(Sidekick.UserSentiment).map((sentiment) => ({
      value: sentiment,
      label: sentiment,
    })),
  },
  isCorsoUser: {
    id: 'isCorsoUser',
    type: FilterControlType.boolean,
    label: 'Is Corso User',
    options: [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ],
  },
};

type FilterValues = {
  [Id in keyof typeof filters]?: (typeof filters)[Id]['value'];
};

const defaultCreatedOn = {
  kind: 'preset',
  preset: RollingDatePreset.last30Days,
} satisfies ViewDateValue;

const columns: ColumnDef<Conversation>[] = [
  { accessorKey: 'name', header: 'Conversation', enableSorting: true },
  {
    id: 'store',
    accessorFn: (conversation) => conversation.store.name,
    header: 'Store',
    enableSorting: true,
  },
  {
    id: 'user',
    accessorFn: ({ user }) =>
      [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
    header: 'User',
    enableSorting: true,
  },
  { accessorKey: 'questionCount', header: 'Messages', enableSorting: true },
  {
    accessorKey: 'costUsd',
    header: 'Cost',
    enableSorting: true,
    cell: ({ row }) => formatter.currency(row.original.costUsd, 'USD'),
  },
  {
    id: 'feedback',
    header: 'Sentiment',
    cell: ({ row }) => (
      <div className="flex max-w-md flex-col gap-1">
        {row.original.feedback.map((feedback) => (
          <div key={feedback.messageId} className="flex items-start gap-2">
            <Badge variant={sentimentVariant[feedback.sentiment]}>
              {feedback.sentiment}
            </Badge>
            {feedback.comment && (
              <span className="text-xs whitespace-normal text-neutral-700">
                {feedback.comment}
              </span>
            )}
          </div>
        ))}
      </div>
    ),
  },
  createdOnColumn<Conversation>({ header: 'Created' }),
];

export default function SidekickConversationsPage() {
  const navigate = useNavigate();
  // merchants' conversations by default; removing the pill shows Corso's too
  const [activeFilters, setActiveFilters] = useState<FilterValues>({
    isCorsoUser: false,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [cursor, setCursor] = useState<{
    direction: 'next' | 'prev';
    id: number;
  } | null>(null);

  const request = {
    createdOn: activeFilters.createdOn ?? defaultCreatedOn,
    sentiments: Object.values(Sidekick.UserSentiment).filter((sentiment) =>
      activeFilters.sentiments?.includes(sentiment),
    ),
    isCorsoUser: activeFilters.isCorsoUser,
    searchTerm: searchTerm || undefined,
    cursor,
  } satisfies CommandAdminApi.RequestBody<
    '/sidekick/conversation/search',
    'post'
  >;

  const query = useQuery({
    queryKey: ['sidekick-conversations', request],
    queryFn: () => api.sidekick.searchConversations(request),
    placeholderData: keepPreviousData,
  });

  const summary = query.data?.summary;

  return (
    <Page title="Sidekick" size="fullWidth">
      <div className="flex flex-col gap-4">
        {summary && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiTile
              label="Cost"
              value={formatter.currency(summary.costUsd, 'USD')}
              description={
                summary.unpricedAnswerCount
                  ? `${formatter.count(summary.unpricedAnswerCount)} of ${formatter.count(summary.answerCount)} answers have no recorded usage`
                  : `${formatter.count(summary.answerCount)} answers`
              }
            />
            <KpiTile
              label="Cost per Message"
              value={formatter.currency(
                summary.questionCount
                  ? summary.costUsd / summary.questionCount
                  : 0,
                'USD',
                false,
                { maximumFractionDigits: 4 },
              )}
            />
            <KpiTile
              label="Conversations"
              value={formatter.count(summary.conversationCount)}
            />
            <KpiTile
              label="Messages"
              value={formatter.count(summary.questionCount)}
              description={`${formatter.count(summary.inputTokens + summary.cacheReadTokens + summary.cacheWriteTokens, true)} input · ${formatter.count(summary.outputTokens, true)} output tokens`}
            />
          </div>
        )}

        <SearchTable
          controls={
            <div className="w-full divide-y divide-neutral-200 bg-white">
              <div className="flex items-center gap-2 p-2">
                <SearchInput
                  defaultValue={searchTerm}
                  placeholder="Search by conversation name or user email"
                  onSearch={(value) => {
                    setCursor(null);
                    setSearchTerm(value);
                  }}
                />
                <SearchSpinner show={query.isFetching} />
              </div>
              <FilterBar
                className="p-2"
                filters={filters}
                activeFilters={{
                  ...activeFilters,
                  createdOn: activeFilters.createdOn ?? defaultCreatedOn,
                }}
                pinned={['createdOn']}
                onChange={(filter) => {
                  // a new filter points the cursor at a different result set
                  setCursor(null);
                  setActiveFilters((current) => ({
                    ...current,
                    [filter.id]: filter.value,
                  }));
                }}
                onRemoveFilters={(ids) => {
                  setCursor(null);
                  setActiveFilters((current) =>
                    Object.fromEntries(
                      Object.entries(current).filter(
                        ([id]) => !ids.some((removed) => removed === id),
                      ),
                    ),
                  );
                }}
              />
            </div>
          }
          data={query.data?.conversations ?? []}
          columns={columns}
          pageInfo={
            query.data?.pageInfo ?? { nextCursor: null, prevCursor: null }
          }
          onPageChange={setCursor}
          onRowClick={(conversation) => navigate(`${conversation.id}`)}
          isFetching={query.isFetching}
          isError={query.isError}
          onRetry={() => {
            query.refetch().catch(console.error);
          }}
          entityLabel="conversations"
          onClearFilters={() => {
            setCursor(null);
            setSearchTerm('');
            setActiveFilters({});
          }}
        />
      </div>
    </Page>
  );
}
